import type { ElementType } from 'react';
import RoadmapsIcon from '@atlaskit/icon-lab/core/roadmaps-plan';
import AppsIcon from '@atlaskit/icon/core/apps';
import AssetsGraph from '@atlaskit/icon-lab/core/assets-graph';
import ClockIcon from '@atlaskit/icon/core/clock';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import FilterIcon from '@atlaskit/icon/core/filter';
import GlobeIcon from '@atlaskit/icon/core/globe';
import GoalsIconOld from '@atlaskit/icon/core/goal';
import MarketplaceIcon from '@atlaskit/icon/core/marketplace';
import HeadphonesIcon from '@atlaskit/icon/core/headphones';
import ProjectIcon from '@atlaskit/icon/core/project';
import PlansIcon from '@atlaskit/icon/core/roadmap';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import TeamsIcon from '@atlaskit/icon/core/teams';
import Avatar from '@atlaskit/icon/core/person-avatar';
import { AssetsIcon } from '@atlaskit/temp-nav-app-icons/assets';
import { GoalsIcon } from '@atlaskit/temp-nav-app-icons/goals';
import ChartTrendIcon from '@atlaskit/icon/core/chart-trend';
import type { MessageDescriptor } from '@atlassian/jira-intl';
import {
	CONFIGURABLE_MENU_ID,
	GLOBAL_APP_MENU_ID,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import OperationsIcon from '@atlassian/jira-operations-icon/src/ui/index.tsx';
import messages from '../messages.tsx';
import { type ConfigurableMenuId, L1_MENU_ID } from '../types.tsx';

export const PACKAGE_NAME = 'jiraNavigationAppsSidebarNav4Common';
export const TEAM_NAME = 'jira-cosmos';
export const MENU_GROUP_MIN_WIDTH = 250;

export const CONFIGURABLE_MENU_IDS = Object.values(CONFIGURABLE_MENU_ID);
export const CONFIGURABLE_MENU_IDS_AS_STRINGS: string[] = CONFIGURABLE_MENU_IDS;

export const GLOBAL_APP_MENU_IDS = Object.values(GLOBAL_APP_MENU_ID);
export const GLOBAL_APP_MENU_IDS_AS_STRINGS: string[] = GLOBAL_APP_MENU_IDS;

export const CONFIGURABLE_MESSAGE_AND_ICON_MAP: {
	[K in ConfigurableMenuId]: { message: MessageDescriptor; Icon: ElementType };
} = {
	[L1_MENU_ID.APPS]: { message: messages.apps, Icon: AppsIcon },
	[L1_MENU_ID.GOALS]: { message: messages.goals, Icon: GoalsIconOld },
	[L1_MENU_ID.ROADMAPS]: { message: messages.roadmaps, Icon: RoadmapsIcon },
	[L1_MENU_ID.PLANS]: { message: messages.plans, Icon: PlansIcon },
	[L1_MENU_ID.PROJECTS]: { message: messages.projects, Icon: ProjectIcon },
	[L1_MENU_ID.FILTERS]: { message: messages.filters, Icon: FilterIcon },
	[L1_MENU_ID.DASHBOARDS]: { message: messages.dashboards, Icon: DashboardIcon },
	[L1_MENU_ID.OVERVIEWS]: { message: messages.overviews, Icon: GlobeIcon },
	[L1_MENU_ID.OPERATIONS]: { message: messages.operations, Icon: OperationsIcon },
	[L1_MENU_ID.ASSETS]: { message: messages.assets, Icon: AssetsGraph },
	[L1_MENU_ID.TEAMS]: { message: messages.teams, Icon: TeamsIcon },
	[L1_MENU_ID.RECENT]: { message: messages.recent, Icon: ClockIcon },
	[L1_MENU_ID.STARRED]: { message: messages.starred, Icon: StarUnstarredIcon },
	[L1_MENU_ID.CUSTOMER_SERVICE]: {
		message: messages.customerService,
		Icon: MarketplaceIcon,
	},
	[L1_MENU_ID.CUSTOMER_HUBS]: {
		message: messages.customerHubs,
		Icon: HeadphonesIcon,
	},
	[L1_MENU_ID.CUSTOMER_SERVICE_ANALYTICS]: {
		message: messages.customerServiceAnalytics,
		Icon: ChartTrendIcon,
	},
	[L1_MENU_ID.GLOBAL_APP_ASSETS]: { message: messages.assets, Icon: AssetsIcon },
	[L1_MENU_ID.GLOBAL_APP_GOALS]: { message: messages.goals, Icon: GoalsIcon },
};

export const FOR_YOU_MESSAGE_AND_ICON_MAP: { message: MessageDescriptor; Icon: ElementType } = {
	message: messages.forYou,
	Icon: Avatar,
};
export const FOR_YOU_MESSAGE_STRING: { message: string } = {
	message: messages.forYou.defaultMessage,
};

export const CONFIGURABLE_MESSAGE_STRING: {
	[K in ConfigurableMenuId]: { message: string };
} = {
	[L1_MENU_ID.APPS]: { message: messages.apps.defaultMessage },
	[L1_MENU_ID.GOALS]: { message: messages.goals.defaultMessage },
	[L1_MENU_ID.ROADMAPS]: { message: messages.roadmaps.defaultMessage },
	[L1_MENU_ID.PLANS]: { message: messages.plans.defaultMessage },
	[L1_MENU_ID.PROJECTS]: { message: messages.projects.defaultMessage },
	[L1_MENU_ID.FILTERS]: { message: messages.filters.defaultMessage },
	[L1_MENU_ID.DASHBOARDS]: { message: messages.dashboards.defaultMessage },
	[L1_MENU_ID.OVERVIEWS]: { message: messages.overviews.defaultMessage },
	[L1_MENU_ID.OPERATIONS]: { message: messages.operations.defaultMessage },
	[L1_MENU_ID.ASSETS]: { message: messages.assets.defaultMessage },
	[L1_MENU_ID.TEAMS]: { message: messages.teams.defaultMessage },
	[L1_MENU_ID.RECENT]: { message: messages.recent.defaultMessage },
	[L1_MENU_ID.STARRED]: { message: messages.starred.defaultMessage },
	[L1_MENU_ID.CUSTOMER_SERVICE]: { message: messages.customerService.defaultMessage },
	[L1_MENU_ID.CUSTOMER_HUBS]: { message: messages.customerHubs.defaultMessage },
	[L1_MENU_ID.CUSTOMER_SERVICE_ANALYTICS]: {
		message: messages.customerServiceAnalytics.defaultMessage,
	},
	[L1_MENU_ID.GLOBAL_APP_ASSETS]: { message: messages.assets.defaultMessage },
	[L1_MENU_ID.GLOBAL_APP_GOALS]: { message: messages.goals.defaultMessage },
};

/**
 * To be used in examples, for example
 */
export const LEFT_SIDEBAR_EXPANDED_MIN_WIDTH = 240; // in pixels
export const LEFT_SIDEBAR_EXPANDED_DEFAULT_WIDTH = 320; // in pixels
export const IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT = false;
export const TMP = 'TEAM_MANAGED_PROJECT';
export const CMP = 'COMPANY_MANAGED_PROJECT';

export const NAV4_OPT_OUT_FEEDBACK_GROUP_ID = 'nav4-opt-out';
export const NAV4_GENERAL_FEEDBACK_GROUP_ID = 'nav4-sidebar';

// used in jsw-expandable-project and board-create
export const NO_OF_BOARDS_TO_SHOW = 25;
